import React, {useEffect, useState} from "react";
import axios from "axios";
import FairDetailsPageContent from "./FairDetailsPageContent";
import { FairInfoBar } from "../../FairInfoBar";
import { handleGenericErrors, handleGenericSuccess } from "../../../utils/ErrorUtility";
import { updateUserAccountId, updateUserDollarBalance, updateUserFairId, updateUserFairStart, updateUserFairEnd, updateUserFlyerCount, updateUserLoginStatus, updateUserSchoolName, updateUserSetUpDate, updatePageType, updateUserFairType } from "../../../DumbleData";
import WarningBar from "../../WarningBar";

interface Props {
	language: string;
}

const FairDetailsPage = ({language}: Props) => {
	const [fairInfo, setFairInfo] = useState<any>();
	const [organization, setOrganization] = useState<any>();
	const [scholasticDollars, setScholasticDollars] = useState<any>();
	const [chairperson, setChairperson] = useState<any>();
	const [consultant, setConsultant] = useState<any>();
	const [homepage, setHomepage] = useState<any>();
	const [coChairs, setCoChairs] = useState();
	const [salesHistory, setSalesHistory] = useState<any>();
  	const [user, setUser] = useState<any>();
	const [banner, setBanner] = useState<any>();
	const [isLoading, setIsLoading] = useState<boolean>(true);

  	useEffect(() => {
		const getFairSettingsPageData = async () => {
				try {
					await axios
						.get(
							`${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/settings`,
							{
								withCredentials: true,
							}
						)
						.then((response) => {
							if (response.status === 200) {
								const { fairInfo, organization, scholasticDollars, chairperson, consultant, homepage, salesHistory, user, banner } = response.data;
								if ( fairInfo ) { setFairInfo(fairInfo); }
								if ( organization ) { setOrganization(organization); }
								if ( scholasticDollars ) { setScholasticDollars(scholasticDollars); }
								if ( chairperson ) { setChairperson(chairperson); }
								if ( consultant ) { setConsultant(consultant); }
								if ( homepage ) { setHomepage(homepage); }
								if ( salesHistory ) { setSalesHistory(salesHistory); }
                				if ( user ) { setUser(user); }
								if ( banner ) { setBanner(banner); }
								updateUserFairId(fairInfo.id);
								updateUserFairType(fairInfo.type);
								updateUserAccountId(organization.bookfairAccountId);
								updateUserLoginStatus(true);
								updatePageType("setup");
								updateUserSetUpDate(fairInfo.setup);
								updateUserFairStart(fairInfo.start);
								updateUserFairEnd(fairInfo.end);
								updateUserDollarBalance(scholasticDollars.balance);
								updateUserSchoolName(fairInfo.name);
								updateUserFlyerCount(fairInfo.fliers.fifthAndBelow, fairInfo.fliers.sixthAndAbove);
								window.scrollTo({
									top: 0,
									left: 0
								});
							} 
							else {
								handleGenericSuccess(response);
							}
						})
						.catch((err) => {
							handleGenericErrors(err);
						});
				} catch (err: any) {
					handleGenericErrors(err);
				} finally {
					setIsLoading(false);
				}
		};
		getFairSettingsPageData();

	}, [
		setFairInfo,
		setOrganization,
    	setScholasticDollars,
		setConsultant,
    	setHomepage,
		setCoChairs,
		setSalesHistory,
		setUser
	]);

	const currentDate = new Date();
	currentDate.setHours(0, 0, 0, 0);

	const isBannerActive =
		banner &&
		banner.bannerStartDate &&
		banner.bannerEndDate &&
		(() => {
			const start = new Date(banner.bannerStartDate);
			const end = new Date(banner.bannerEndDate);
			start.setHours(0, 0, 0, 0);
			end.setHours(23, 59, 59, 999);
			return start <= currentDate && currentDate <= end;
		})();

	const bannerInfo =
		language === "en"
			? banner?.english
			: (banner?.french && Object.values(banner.french).some(Boolean))
				? banner.french
				: banner?.english;

	const getFullLink = (link: string) => {
		if (!link.startsWith("http://") && !link.startsWith("https://")) {
			return `https://${link}`;
		}
		return link;
	};

	const modifiedBannerText = (
		<p>
			{bannerInfo?.text}{" "}
			{bannerInfo?.link && bannerInfo?.linkText && (
				<a href={getFullLink(bannerInfo.link)} target="_blank" rel="noopener noreferrer">{bannerInfo.linkText}</a>
			)}
		</p>
	);

	return (
		<>
		{!isLoading && (<>
			{isBannerActive && (
				<WarningBar
					heading={bannerInfo?.headline}
					body={modifiedBannerText}
				/>
			)}
			<FairInfoBar
				language={language}
				enableSwitch={user?.enableSwitch}
				fairInfoData={fairInfo}
				organizationData={fairInfo}
				pageType={"fair-details"}
			/>
			<FairDetailsPageContent
				fairInfo={fairInfo}
				organization={organization}
                scholasticDollars={scholasticDollars}
                chairperson={chairperson}
				consultant={consultant}
                homepage={homepage}
				coChairs={coChairs}
				language={language}
				salesHistory={salesHistory}
			/> 		
		</>)}
		</>
	);
};

export default FairDetailsPage;
